import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import ToastMessage from '../../../shared-components/toast-message/ToastMessage';
import {
  getLocalStorageItem,
  removeLocalStorageItem,
  setLocalStorageItem,
} from '../../../shared-functions/LocalStorage';
import charger from '../../../utils/api/charger';
import { generateInvoicePdf } from '../../../utils/generateInvoicePdf';
import '../scss/invoice.scss';

interface CdrSummary {
  cdrId?: string;
  externalId?: string;
  startTime?: string;
  endTime?: string;
  totalEnergy?: number | string;
  totalTime?: number | string;
  currency?: string;
  totalCost?: number | string;
  finalAmount?: number | string;
  isPaid?: boolean;
  paymentStatus?: 'SUCCESS' | 'FAILED' | string | null;
  paymentFailedReason?: string | null;
  paymentMethod?: 'card' | 'wallet' | string | null;
  paidAt?: string;
  isInvoiceGenerated?: boolean;
  invoiceNumber?: string;
  platformFee?: number | string;
  remark?: string;
}

interface InvoiceDetailData {
  _id?: string;
  invoiceNumber?: string;
  invoiceDate?: string;
  partyId?: string;
  currency?: string;
  createdAt?: string;
  updatedAt?: string;
  remark?: string;
  invoiceFrom?: {
    name?: string;
    mobile?: string;
    email?: string;
    gstin?: string;
    cin?: string;
    billingAddress?: string;
    sacCode?: string;
  };
  invoiceTo?: {
    name?: string;
    mobile?: string;
    email?: string;
    pan?: string;
    chargingLocation?: string;
    billingAddress?: string;
    address?: string;
  };
  chargerInfo?: {
    stationId?: string;
    chargerId?: string;
    connectorId?: string | number;
    connectorType?: string;
    connectorRating?: string;
    accessType?: string;
  };
  chargingDetails?: {
    startTime?: string;
    endTime?: string;
    chargingDate?: string;
    energyUsed?: number | string;
    energyOrdered?: number | string;
    chargingDuration?: string;
    invoiceAmountPaid?: number | string;
    costBreakdown?: {
      consumptionCost?: number | string;
      platformFee?: number | string;
      paymentSurcharge?: number | string;
      total?: number | string;
      currency?: string;
    };
    evVin?: string;
    evNumber?: string;
    bookingId?: string;
    remark?: string;
  };
  sessionId?: string;
  transactionId?: string;
  userCustomId?: string;
  financialYear?: string;
  invoiceTime?: string;
  costBreakdown?: {
    connectorPrice?: number | string;
    unitConsumed?: number | string;
    totalAmount?: number | string;
    consumptionCost?: number | string;
    platformFee?: number | string;
    paymentSurcharge?: number | string;
    total?: number | string;
  };
}

export interface CountryOption {
  code: string;
  country: string;
  name: string;
  flag: string;
}

export interface InvoiceFormValues {
  name: string;
  email: string;
  mobile: string;
  address: string;
}

export interface InvoiceFormErrors {
  name?: string;
  email?: string;
  mobile?: string;
  address?: string;
}

export const COUNTRY_CODES: CountryOption[] = [
  { code: '+91', country: 'IN', name: 'India', flag: '🇮🇳' },
  { code: '+966', country: 'SA', name: 'Saudi Arabia', flag: '🇸🇦' },
  { code: '+971', country: 'AE', name: 'United Arab Emirates', flag: '🇦🇪' },
  { code: '+974', country: 'QA', name: 'Qatar', flag: '🇶🇦' },
  { code: '+965', country: 'KW', name: 'Kuwait', flag: '🇰🇼' },
  { code: '+973', country: 'BH', name: 'Bahrain', flag: '🇧🇭' },
  { code: '+968', country: 'OM', name: 'Oman', flag: '🇴🇲' },
  { code: '+1', country: 'US', name: 'USA / Canada', flag: '🇺🇸' },
  { code: '+44', country: 'GB', name: 'United Kingdom', flag: '🇬🇧' },
  { code: '+20', country: 'EG', name: 'Egypt', flag: '🇪🇬' },
  { code: '+49', country: 'DE', name: 'Germany', flag: '🇩🇪' },
  { code: '+33', country: 'FR', name: 'France', flag: '🇫🇷' },
  { code: '+65', country: 'SG', name: 'Singapore', flag: '🇸🇬' },
  { code: '+61', country: 'AU', name: 'Australia', flag: '🇦🇺' },
  { code: '+60', country: 'MY', name: 'Malaysia', flag: '🇲🇾' },
  { code: '+92', country: 'PK', name: 'Pakistan', flag: '🇵🇰' },
  { code: '+880', country: 'BD', name: 'Bangladesh', flag: '🇧🇩' },
  { code: '+94', country: 'LK', name: 'Sri Lanka', flag: '🇱🇰' },
  { code: '+977', country: 'NP', name: 'Nepal', flag: '🇳🇵' },
  { code: '+63', country: 'PH', name: 'Philippines', flag: '🇵🇭' },
  { code: '+62', country: 'ID', name: 'Indonesia', flag: '🇮🇩' },
  { code: '+84', country: 'VN', name: 'Vietnam', flag: '🇻🇳' },
  { code: '+86', country: 'CN', name: 'China', flag: '🇨🇳' },
  { code: '+81', country: 'JP', name: 'Japan', flag: '🇯🇵' },
  { code: '+82', country: 'KR', name: 'South Korea', flag: '🇰🇷' },
  { code: '+90', country: 'TR', name: 'Turkey', flag: '🇹🇷' },
  { code: '+962', country: 'JO', name: 'Jordan', flag: '🇯🇴' },
  { code: '+961', country: 'LB', name: 'Lebanon', flag: '🇱🇧' },
  { code: '+212', country: 'MA', name: 'Morocco', flag: '🇲🇦' },
  { code: '+27', country: 'ZA', name: 'South Africa', flag: '🇿🇦' },
  { code: '+39', country: 'IT', name: 'Italy', flag: '🇮🇹' },
  { code: '+34', country: 'ES', name: 'Spain', flag: '🇪🇸' },
  { code: '+31', country: 'NL', name: 'Netherlands', flag: '🇳🇱' },
  { code: '+41', country: 'CH', name: 'Switzerland', flag: '🇨🇭' },
  { code: '+46', country: 'SE', name: 'Sweden', flag: '🇸🇪' },
  { code: '+47', country: 'NO', name: 'Norway', flag: '🇳🇴' },
  { code: '+45', country: 'DK', name: 'Denmark', flag: '🇩🇰' },
  { code: '+353', country: 'IE', name: 'Ireland', flag: '🇮🇪' },
  { code: '+64', country: 'NZ', name: 'New Zealand', flag: '🇳🇿' },
  { code: '+55', country: 'BR', name: 'Brazil', flag: '🇧🇷' },
  { code: '+52', country: 'MX', name: 'Mexico', flag: '🇲🇽' },
];

/**
 * Format ISO or UTC date string into localized MMM d, y • h:mm a
 * @param dateString Date string
 * @returns Formatted localized string
 */
const formatLocalTime = (dateString?: string | null): string => {
  if (!dateString) return '';
  try {
    const isIsoOrUtc =
      dateString.endsWith('Z') || dateString.includes('+') || /T\d{2}:\d{2}:\d{2}/.test(dateString);
    const parsed = isIsoOrUtc ? new Date(dateString) : new Date(`${dateString}Z`);
    if (Number.isNaN(parsed.getTime())) return '';

    return parsed.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return '';
  }
};

/**
 * Format totalTime (hours) to "45 min" or "1h 05m"
 * @param hours Total time in hours
 * @returns Formatted duration string
 */
const formatDurationHours = (hours?: number | string | null): string => {
  if (hours === undefined || hours === null || hours === '') return '';
  const numericValue = Number(hours);
  if (Number.isNaN(numericValue) || numericValue <= 0) return '0 min';

  if (numericValue < 1) {
    const minutes = Math.max(1, Math.round(numericValue * 60));
    return `${minutes} min`;
  }
  const fullHours = Math.floor(numericValue);
  const remainderMinutes = Math.round((numericValue - fullHours) * 60);
  return `${fullHours}h ${String(remainderMinutes).padStart(2, '0')}m`;
};

/**
 * Format date into DD-MM-YYYY format
 * @param dateString Date string
 * @returns Formatted date string
 */
const formatDateOnly = (dateString?: string | null): string => {
  if (!dateString) return '';
  try {
    const isIsoOrUtc =
      dateString.endsWith('Z') || dateString.includes('+') || /T\d{2}:\d{2}:\d{2}/.test(dateString);
    const parsed = isIsoOrUtc ? new Date(dateString) : new Date(`${dateString}Z`);
    if (Number.isNaN(parsed.getTime())) return '';
    const day = String(parsed.getDate()).padStart(2, '0');
    const month = String(parsed.getMonth() + 1).padStart(2, '0');
    const year = parsed.getFullYear();
    return `${day}-${month}-${year}`;
  } catch {
    return '';
  }
};

/**
 * Calculate financial year string (e.g., "2026-27")
 * @param dateString Date string
 * @returns Financial year string
 */
const calculateFinancialYear = (dateString?: string | null): string => {
  try {
    const date = dateString ? new Date(dateString) : new Date();
    if (Number.isNaN(date.getTime())) return '2026-27';
    const year = date.getFullYear();
    const month = date.getMonth();
    if (month >= 3) {
      const nextYear = String((year + 1) % 100).padStart(2, '0');
      return `${year}-${nextYear}`;
    }
    const previousYear = year - 1;
    const currentYear = String(year % 100).padStart(2, '0');
    return `${previousYear}-${currentYear}`;
  } catch {
    return '2026-27';
  }
};

/**
 * Checks if a value is non-empty and not a '-' placeholder
 * @param value Value to check
 * @returns boolean
 */
const isValidValue = (value?: string | number | null): boolean => {
  if (value === undefined || value === null) return false;
  const stringValue = String(value).trim();
  return stringValue !== '' && stringValue !== '-';
};

/**
 * Returns the value if valid, or a fallback if invalid/empty/'-'
 * @param value Value to check
 * @param fallback Optional fallback value
 * @returns Valid value or fallback
 */
const getValidValue = <T,>(value: T, fallback?: T): T | undefined => {
  if (value === undefined || value === null) return fallback;
  const stringValue = String(value).trim();
  if (stringValue === '' || stringValue === '-') return fallback;
  return value;
};

/**
 * Checks whether a session remark indicates a fault, error, or abnormal condition
 * @param remark Remark string to inspect
 * @returns Boolean indicating if the remark is warning-level
 */
const isWarningRemark = (remark?: string | null): boolean => {
  if (!remark) return false;
  const lower = remark.toLowerCase();
  return (
    lower.includes('fault') ||
    lower.includes('error') ||
    lower.includes('fail') ||
    lower.includes('emerg') ||
    lower.includes('abort') ||
    lower.includes('interrupt') ||
    lower.includes('timeout') ||
    lower.includes('abnormal') ||
    lower.includes('unavailable') ||
    lower.includes('suspend')
  );
};

/**
 * Cleans up local session storage when exiting the invoice
 */
const cleanStorageAndRedirect = () => {
  const domain =
    getLocalStorageItem('domain') || new URLSearchParams(window.location.search).get('domain');

  removeLocalStorageItem('token');
  removeLocalStorageItem('adhocToken');
  removeLocalStorageItem('adhocUid');
  removeLocalStorageItem('sessionId');
  removeLocalStorageItem('paymentMethod');
  removeLocalStorageItem('customer.browser.deviceId');
  removeLocalStorageItem('chargingStatus');
  removeLocalStorageItem('guestToken');
  removeLocalStorageItem('selectedConnector');
  removeLocalStorageItem('paidAmount');
  removeLocalStorageItem('totalKwhConsumer');
  removeLocalStorageItem('liveCost');
  removeLocalStorageItem('liveHours');
  removeLocalStorageItem('liveMinutes');
  removeLocalStorageItem('liveSeconds');
  removeLocalStorageItem('currency');
  removeLocalStorageItem('idleState');
  removeLocalStorageItem('invoiceRemove');

  const redirectUrl = domain
    ? `${window.location.pathname}?domain=${encodeURIComponent(domain)}`
    : window.location.pathname;

  window.location.href = redirectUrl;
};

/**
 * Charging Summary & Invoice Component
 * @returns JSX.Element
 */
function Invoice() {
  const { t } = useTranslation('charger');
  const { deviceId } = useParams<{ deviceId: string }>();

  // Snapshot fallback data from local storage
  const snapshotPaid = Number(getLocalStorageItem('paidAmount')) || 0;
  const snapshotKwh = Number(getLocalStorageItem('totalKwhConsumer')) || 0;
  const snapshotHours = Number(getLocalStorageItem('liveHours')) || 0;
  const snapshotMinutes = Number(getLocalStorageItem('liveMinutes')) || 0;
  const snapshotCurrency = getLocalStorageItem('currency') || 'SAR';
  const storedSessionId = getLocalStorageItem('sessionId') || '';

  useEffect(() => {
    setLocalStorageItem('invoiceRemove', true);
  }, []);

  const [summary, setSummary] = useState<CdrSummary | null>(null);
  const [cdrLoading, setCdrLoading] = useState<boolean>(Boolean(storedSessionId));
  const [activeView, setActiveView] = useState<'receipt' | 'invoice_detail'>('receipt');
  const [invoiceData, setInvoiceData] = useState<InvoiceDetailData | null>(null);
  const [invoiceLoading, setInvoiceLoading] = useState<boolean>(false);
  const [toastStatus, setToastStatus] = useState({
    openToast: false,
    showToastMessage: '',
  });

  // Invoice Form Modal States
  const [showInvoiceModal, setShowInvoiceModal] = useState<boolean>(false);
  const [formData, setFormData] = useState<InvoiceFormValues>({
    name: '',
    email: '',
    mobile: '',
    address: '',
  });
  const [formErrors, setFormErrors] = useState<InvoiceFormErrors>({});
  const [selectedCountry, setSelectedCountry] = useState<CountryOption>(
    COUNTRY_CODES.find((item) =>
      snapshotCurrency === 'SAR' ? item.country === 'SA' : item.country === 'IN',
    ) || COUNTRY_CODES[0],
  );
  const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState<boolean>(false);
  const [countrySearch, setCountrySearch] = useState<string>('');
  const countryDropdownReference = useRef<HTMLDivElement>(null);

  const pollStartedReference = useRef(false);

  /**
   * Close country dropdown on click outside
   * @param event Mouse click event
   */
  const handleClickOutside = (event: MouseEvent) => {
    if (
      countryDropdownReference.current &&
      !countryDropdownReference.current.contains(event.target as Node)
    ) {
      setIsCountryDropdownOpen(false);
    }
  };

  /**
   * Effect to listen for click outside to close dropdown
   */
  useEffect(() => {
    if (isCountryDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isCountryDropdownOpen]);

  /**
   * Acquire & Settle Polling Loop for Session CDR
   */
  useEffect(() => {
    if (!storedSessionId || pollStartedReference.current) {
      if (!storedSessionId) {
        setCdrLoading(false);
      }
      return undefined;
    }
    pollStartedReference.current = true;

    let isMounted = true;
    let pollTimer: NodeJS.Timeout | null = null;
    const startTime = Date.now();

    /**
     * Poll function to acquire CDR summary
     */
    const pollCdr = async () => {
      if (!isMounted) return;
      const elapsedMs = Date.now() - startTime;

      // 5-minute timeout give up
      if (elapsedMs >= 300000) {
        if (isMounted) setCdrLoading(false);
        return;
      }

      try {
        const response = await charger.fetchSessionCdr(storedSessionId);
        const data = response?.data?.data || response?.data;

        if (data?.cdrGenerated && data?.summary) {
          if (isMounted) {
            setSummary(data.summary);
            setCdrLoading(false);
          }

          // Check if settled
          const isSettled =
            data.summary.paymentStatus !== null &&
            (data.summary.paymentStatus === 'FAILED' ||
              data.summary.paymentStatus === 'NOT_APPLICABLE' ||
              data.summary.isInvoiceGenerated === true);

          if (!isSettled && elapsedMs < 30000) {
            // Settle loop: poll every 5s up to 30s
            pollTimer = setTimeout(pollCdr, 5000);
            return;
          }
          return;
        }
      } catch (error: any) {
        // Only 4xx stops polling; 5xx / connection issues retry silently
        const statusCode = error?.response?.status;
        if (statusCode >= 400 && statusCode < 500) {
          if (isMounted) setCdrLoading(false);
          return;
        }
      }

      // Next poll interval: 5s for first 2.5m, then 30s
      const nextInterval = elapsedMs < 150000 ? 5000 : 30000;
      pollTimer = setTimeout(pollCdr, nextInterval);
    };

    pollCdr();

    return () => {
      isMounted = false;
      if (pollTimer) clearTimeout(pollTimer);
    };
  }, [storedSessionId]);

  const currencyCode = summary?.currency || snapshotCurrency;
  const energyKwhDisplay = `${Number(summary?.totalEnergy ?? snapshotKwh).toFixed(3)} kWh`;

  const formattedStartTime = formatLocalTime(summary?.startTime);
  const formattedEndTime = formatLocalTime(summary?.endTime);
  const formattedPaidAt = formatLocalTime(summary?.paidAt || summary?.endTime);

  const paymentMethodLabel = summary?.paymentMethod === 'wallet' ? t('paidWallet') : t('paidCard');
  const isPaymentSuccess =
    summary?.paymentStatus?.toUpperCase() === 'SUCCESS' || summary?.isPaid === true;

  /**
   * Filter countries list by search text
   */
  const filteredCountries = COUNTRY_CODES.filter(
    (item) =>
      item.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
      item.code.includes(countrySearch) ||
      item.country.toLowerCase().includes(countrySearch.toLowerCase()),
  );

  /**
   * Handle invoice form input change
   */
  const handleInputChange = (field: keyof InvoiceFormValues, value: string) => {
    let sanitizedValue = value;
    if (field === 'mobile') {
      // Only keep digits for phone number
      sanitizedValue = value.replace(/\D/g, '');
    }

    setFormData((previous) => ({
      ...previous,
      [field]: sanitizedValue,
    }));

    if (formErrors[field]) {
      setFormErrors((previous) => ({
        ...previous,
        [field]: undefined,
      }));
    }
  };

  /**
   * Validates invoice form inputs
   */
  const validateInvoiceForm = (): boolean => {
    const errors: InvoiceFormErrors = {};
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    if (!formData.name.trim()) {
      errors.name = t('fullNameRequired');
    }

    if (!formData.email.trim()) {
      errors.email = t('emailRequired');
    } else if (!emailRegex.test(formData.email.trim())) {
      errors.email = t('emailInvalid');
    }

    if (!formData.mobile.trim()) {
      errors.mobile = t('mobileRequired');
    } else if (formData.mobile.trim().length < 6 || formData.mobile.trim().length > 15) {
      errors.mobile = t('mobileInvalid');
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  /**
   * Opens the Invoice details form modal
   */
  const handleOpenInvoice = () => {
    setFormErrors({});
    setShowInvoiceModal(true);
  };

  /**
   * Submits invoice form and triggers invoice API with POST method
   */
  const handleFormSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!validateInvoiceForm()) {
      return;
    }

    const cdrId = summary?.cdrId;
    if (!cdrId) {
      setToastStatus({
        openToast: true,
        showToastMessage: t('sessionRefNotFound'),
      });
      return;
    }

    const cleanedMobile = formData.mobile.trim().replace(/\D/g, '');
    const payload = {
      name: formData.name.trim(),
      email: formData.email.trim(),
      mobile: `${selectedCountry.code}${cleanedMobile}`,
      address: formData.address.trim(),
    };

    setInvoiceLoading(true);
    try {
      const response = await charger.fetchCdrInvoice(cdrId, payload);
      const rawData = response?.data?.data || response?.data;
      if (rawData) {
        if (!rawData.invoiceTo) {
          rawData.invoiceTo = {};
        }
        rawData.invoiceTo.name = getValidValue(rawData.invoiceTo.name, payload.name);
        rawData.invoiceTo.email = getValidValue(rawData.invoiceTo.email, payload.email);
        rawData.invoiceTo.mobile = getValidValue(rawData.invoiceTo.mobile, payload.mobile);
        rawData.invoiceTo.billingAddress = getValidValue(
          rawData.invoiceTo.billingAddress,
          payload.address,
        );

        if (!rawData.invoiceFrom) {
          rawData.invoiceFrom = {};
        }
        rawData.invoiceFrom.name = getValidValue(rawData.invoiceFrom.name, 'Energic');
        rawData.invoiceFrom.email = getValidValue(rawData.invoiceFrom.email, 'admin@energic.com');
        rawData.invoiceFrom.billingAddress = getValidValue(
          rawData.invoiceFrom.billingAddress,
          'Coimbatore, India 637101',
        );

        rawData.remark = getValidValue(rawData.remark, summary?.remark);

        setInvoiceData(rawData);
      }
      setShowInvoiceModal(false);
      setActiveView('invoice_detail');
    } catch {
      // Fallback with user provided data
      setInvoiceData({
        invoiceNumber: summary?.invoiceNumber || '',
        invoiceDate: summary?.paidAt || summary?.endTime || '',
        financialYear: calculateFinancialYear(summary?.paidAt || summary?.endTime),
        transactionId: summary?.externalId || '',
        sessionId: storedSessionId || '',
        remark: summary?.remark,
        invoiceFrom: {
          name: 'Energic',
          email: 'admin@energic.com',
          billingAddress: 'Coimbatore, India 637101',
        },
        invoiceTo: {
          name: payload.name,
          email: payload.email,
          mobile: payload.mobile,
          billingAddress: payload.address,
          address: payload.address,
        },
        chargerInfo: {
          chargerId: deviceId,
        },
        chargingDetails: {
          energyUsed: summary?.totalEnergy !== undefined ? Number(summary.totalEnergy) : undefined,
          chargingDuration: formatDurationHours(summary?.totalTime),
          costBreakdown: {
            consumptionCost:
              summary?.totalCost !== undefined ? Number(summary.totalCost) : undefined,
            total: summary?.finalAmount !== undefined ? Number(summary.finalAmount) : undefined,
            currency: summary?.currency || currencyCode,
          },
        },
      });
      setShowInvoiceModal(false);
      setActiveView('invoice_detail');
    } finally {
      setInvoiceLoading(false);
    }
  };

  /**
   * Helper to conditionally render an info row only if valid value exists
   */
  const renderRow = (label: string, value?: string | number | null) => {
    if (value === undefined || value === null || value === '' || value === '-') {
      return null;
    }
    return (
      <div className="info-row" key={label}>
        <span className="info-label">{label}</span>
        <span className="info-value">{value}</span>
      </div>
    );
  };

  /**
   * Helper to conditionally render a cost row
   */
  const renderCostRow = (
    label: string,
    value?: string | number | null,
    currencyLabel = currencyCode,
  ) => {
    if (value === undefined || value === null || value === '' || value === '-') {
      return null;
    }
    return (
      <div className="cost-row" key={label}>
        <span className="cost-label">{label}</span>
        <span className="cost-val">
          {value} {currencyLabel}
        </span>
      </div>
    );
  };

  /**
   * Calculate headline total amount
   * @returns Formatted headline amount
   */
  const getHeadlineAmount = useCallback(() => {
    if (summary?.paymentStatus === 'NOT_APPLICABLE') {
      return '0.00';
    }
    if (summary?.finalAmount !== undefined && summary?.finalAmount !== null) {
      return Number(summary.finalAmount).toFixed(2);
    }
    if (snapshotPaid > 0) {
      return snapshotPaid.toFixed(2);
    }
    return '--';
  }, [summary?.paymentStatus, summary?.finalAmount, snapshotPaid]);

  /**
   * Calculate duration display
   * @returns Formatted duration string
   */
  const getDurationDisplay = useCallback(() => {
    if (summary?.totalTime) {
      return formatDurationHours(summary.totalTime);
    }
    if (snapshotHours > 0 || snapshotMinutes > 0) {
      const hoursPart = snapshotHours > 0 ? `${snapshotHours}h ` : '';
      return `${hoursPart}${snapshotMinutes} min`;
    }
    return '0 min';
  }, [summary?.totalTime, snapshotHours, snapshotMinutes]);

  /**
   * Render payment status badge
   * @returns JSX Element
   */
  const renderPaymentStatusBadge = () => {
    if (summary?.paymentStatus === 'NOT_APPLICABLE') {
      return (
        <div className="payment-status-badge">
          <svg
            className="badge-check"
            viewBox="0 0 24 24"
            fill="var(--primary)"
            stroke="var(--white)"
            strokeWidth="2">
            <circle cx="12" cy="12" r="10" fill="var(--primary)" />
            <path d="M8 12l2.5 2.5L16 9" stroke="var(--white)" fill="none" />
          </svg>
          <span>{t('freeCharging') || 'Free'}</span>
        </div>
      );
    }
    if (summary?.paymentStatus === 'FAILED') {
      return (
        <div className="payment-status-badge failed">
          <span>✕ {t('paymentFailed')}</span>
        </div>
      );
    }
    if (summary?.paymentStatus === null) {
      return (
        <div className="payment-status-badge processing">
          <span>● {t('paymentProcessing')}</span>
        </div>
      );
    }
    return (
      <>
        <div className="payment-status-badge">
          <svg
            className="badge-check"
            viewBox="0 0 24 24"
            fill="var(--primary)"
            stroke="var(--white)"
            strokeWidth="2">
            <circle cx="12" cy="12" r="10" fill="var(--primary)" />
            <path d="M8 12l2.5 2.5L16 9" stroke="var(--white)" fill="none" />
          </svg>
          <span>{paymentMethodLabel}</span>
        </div>
        {formattedPaidAt && <p className="payment-date-text">{formattedPaidAt}</p>}
      </>
    );
  };

  let invoiceSessionTime = '';
  if (invoiceData?.chargingDetails?.startTime && invoiceData?.chargingDetails?.endTime) {
    invoiceSessionTime = `${formatLocalTime(invoiceData.chargingDetails.startTime)} to ${formatLocalTime(
      invoiceData.chargingDetails.endTime,
    )}`;
  } else if (formattedStartTime && formattedEndTime) {
    invoiceSessionTime = `${formattedStartTime} to ${formattedEndTime}`;
  }

  /**
   * Generates and downloads the invoice PDF using jsPDF matching the template format
   */
  const handleDownloadPdf = () => {
    try {
      const invoiceNumber =
        getValidValue(invoiceData?.invoiceNumber) ||
        getValidValue(summary?.invoiceNumber) ||
        'FY2026-27/ENR/0026';

      const invoiceDate = invoiceData?.invoiceDate
        ? formatLocalTime(invoiceData.invoiceDate)
        : invoiceData?.chargingDetails?.chargingDate ||
          formattedEndTime ||
          formatLocalTime(new Date().toISOString());

      const financialYear =
        getValidValue(invoiceData?.financialYear) ||
        calculateFinancialYear(invoiceData?.invoiceDate || summary?.endTime);

      const referenceNo =
        getValidValue(invoiceData?.transactionId) ||
        getValidValue(invoiceData?.sessionId) ||
        getValidValue(summary?.externalId) ||
        getValidValue(storedSessionId) ||
        '';

      const chargingDate =
        getValidValue(invoiceData?.chargingDetails?.chargingDate) ||
        formatDateOnly(
          invoiceData?.chargingDetails?.startTime ||
            invoiceData?.invoiceDate ||
            summary?.startTime ||
            summary?.endTime,
        );

      const chargingTime = invoiceSessionTime;

      const currency =
        getValidValue(invoiceData?.chargingDetails?.costBreakdown?.currency) ||
        getValidValue(invoiceData?.currency) ||
        getValidValue(summary?.currency) ||
        currencyCode;

      generateInvoicePdf({
        invoiceNumber,
        invoiceDate,
        financialYear,
        referenceNo,
        seller: {
          name: getValidValue(invoiceData?.invoiceFrom?.name, 'Energic'),
          email: getValidValue(invoiceData?.invoiceFrom?.email, 'admin@energic.com'),
          billingAddress: getValidValue(invoiceData?.invoiceFrom?.billingAddress),
          mobile: getValidValue(invoiceData?.invoiceFrom?.mobile),
          gstin: getValidValue(invoiceData?.invoiceFrom?.gstin),
          cin: getValidValue(invoiceData?.invoiceFrom?.cin),
          sacCode: getValidValue(invoiceData?.invoiceFrom?.sacCode),
        },
        buyer: {
          name: getValidValue(invoiceData?.invoiceTo?.name, formData.name) || 'Customer',
          email: getValidValue(invoiceData?.invoiceTo?.email, formData.email),
          mobile:
            getValidValue(invoiceData?.invoiceTo?.mobile) ||
            (formData.mobile ? `${selectedCountry.code}${formData.mobile}` : undefined),
          address:
            getValidValue(invoiceData?.invoiceTo?.billingAddress) ||
            getValidValue(invoiceData?.invoiceTo?.address) ||
            getValidValue(formData.address),
          pan: getValidValue(invoiceData?.invoiceTo?.pan),
          chargingLocation:
            getValidValue(invoiceData?.invoiceTo?.chargingLocation) ||
            getValidValue(invoiceData?.invoiceTo?.billingAddress) ||
            getValidValue(invoiceData?.invoiceTo?.address) ||
            getValidValue(formData.address),
        },
        session: {
          charger: getValidValue(invoiceData?.chargerInfo?.chargerId, deviceId),
          connector:
            [
              invoiceData?.chargerInfo?.connectorId,
              invoiceData?.chargerInfo?.connectorType,
              invoiceData?.chargerInfo?.connectorRating,
            ]
              .filter(isValidValue)
              .join(' / ') || '1 / AC_3_PHASE',
          date: chargingDate,
          time: chargingTime,
          duration:
            getValidValue(invoiceData?.chargingDetails?.chargingDuration) || getDurationDisplay(),
          energy: isValidValue(invoiceData?.chargingDetails?.energyUsed)
            ? `${invoiceData?.chargingDetails?.energyUsed} kWh`
            : energyKwhDisplay,
          evVin: getValidValue(invoiceData?.chargingDetails?.evVin),
          evNumber: getValidValue(invoiceData?.chargingDetails?.evNumber),
          bookingId: getValidValue(invoiceData?.chargingDetails?.bookingId),
          remark:
            getValidValue(invoiceData?.remark) ||
            getValidValue(invoiceData?.chargingDetails?.remark) ||
            getValidValue(summary?.remark),
        },
        costs: {
          connectorPrice: getValidValue(invoiceData?.costBreakdown?.connectorPrice),
          consumptionCost:
            getValidValue(invoiceData?.chargingDetails?.costBreakdown?.consumptionCost) ??
            getValidValue(invoiceData?.costBreakdown?.consumptionCost) ??
            (summary?.totalCost !== undefined ? Number(summary.totalCost).toFixed(2) : undefined),
          platformFee:
            getValidValue(invoiceData?.chargingDetails?.costBreakdown?.platformFee) ??
            getValidValue(invoiceData?.costBreakdown?.platformFee),
          sessionFee:
            getValidValue(invoiceData?.chargingDetails?.costBreakdown?.paymentSurcharge) ??
            getValidValue(invoiceData?.costBreakdown?.paymentSurcharge),
        },
        totalAmount:
          getValidValue(invoiceData?.chargingDetails?.costBreakdown?.total) ??
          getValidValue(invoiceData?.chargingDetails?.invoiceAmountPaid) ??
          getValidValue(invoiceData?.costBreakdown?.totalAmount) ??
          getHeadlineAmount(),
        currency,
      });
    } catch (error) {
      console.error('Failed to generate PDF:', error);
      setToastStatus({
        openToast: true,
        showToastMessage: t('failedGeneratePdf'),
      });
    }
  };

  return (
    <div className="invoice-wrap">
      <ToastMessage
        open={toastStatus.openToast}
        onClose={() => setToastStatus({ openToast: false, showToastMessage: '' })}
        type="error"
        message={toastStatus.showToastMessage || 'Error'}
        onCloseTimer={6000}
      />

      {/* --- 1. RECEIPT SUMMARY VIEW (Charging Completed) --- */}
      {activeView === 'receipt' && (
        <div className="receipt-container">
          <div className="receipt-top-content">
            <div className="completed-icon-circle">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="var(--primary)"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <path d="M8 12l2.5 2.5L16 9" />
              </svg>
            </div>

            <h1 className="completed-title">{t('chargingCompleted')}</h1>

            {cdrLoading ? (
              <div className="receipt-loader-box">
                <div className="receipt-spinner" />
                <p className="loading-receipt-text">{t('finalizingReceipt')}</p>
              </div>
            ) : (
              <>
                <div className="total-paid-section">
                  <p className="total-paid-label">{t('totalPaid')}</p>
                  <h2 className="total-paid-amount">
                    {getHeadlineAmount()} {currencyCode}
                  </h2>
                </div>

                {renderPaymentStatusBadge()}

                {summary?.remark && (
                  <div
                    className={`receipt-remark-banner ${
                      isWarningRemark(summary.remark) ? 'is-warning' : ''
                    }`}>
                    <div className="remark-icon-box">
                      {isWarningRemark(summary.remark) ? (
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round">
                          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                          <line x1="12" y1="9" x2="12" y2="13" />
                          <line x1="12" y1="17" x2="12.01" y2="17" />
                        </svg>
                      ) : (
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round">
                          <circle cx="12" cy="12" r="10" />
                          <line x1="12" y1="16" x2="12" y2="12" />
                          <line x1="12" y1="8" x2="12.01" y2="8" />
                        </svg>
                      )}
                    </div>
                    <div className="remark-content">
                      <p className="remark-text">{summary.remark}</p>
                    </div>
                  </div>
                )}

                {/* Details List */}
                <div className="receipt-details-list">
                  <div className="receipt-row">
                    <span className="row-label">{t('liveWatts')}</span>
                    <span className="row-value">{energyKwhDisplay}</span>
                  </div>

                  <div className="receipt-row">
                    <span className="row-label">{t('duration')}</span>
                    <span className="row-value">{getDurationDisplay()}</span>
                  </div>

                  {formattedStartTime && (
                    <div className="receipt-row">
                      <span className="row-label">{t('start')}</span>
                      <span className="row-value">{formattedStartTime}</span>
                    </div>
                  )}

                  {formattedEndTime && (
                    <div className="receipt-row">
                      <span className="row-label">{t('end')}</span>
                      <span className="row-value">{formattedEndTime}</span>
                    </div>
                  )}

                  {summary?.totalCost !== undefined && (
                    <div className="receipt-row">
                      <span className="row-label">{t('energyCost')}</span>
                      <span className="row-value">
                        {Number(summary.totalCost).toFixed(2)} {currencyCode}
                      </span>
                    </div>
                  )}

                  {/* {summary?.finalAmount !== undefined && summary?.totalCost !== undefined && (
                    <div className="receipt-row">
                      <span className="row-label">{t('platformFee')}</span>
                      <span className="row-value">
                        {Number(summary.platformFee).toFixed(2)} {currencyCode}
                      </span>
                    </div>
                  )} */}
                </div>

                {/* References */}
                <div className="receipt-reference-notes">
                  {summary?.externalId && (
                    <p className="ref-text">
                      {t('referenceNo')}: {summary.externalId}
                    </p>
                  )}
                  {summary?.invoiceNumber && (
                    <p className="ref-text">
                      {t('invoiceNo')}: {summary.invoiceNumber}
                    </p>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Action Buttons at Bottom */}
          <div className="receipt-actions">
            {Boolean(
              isPaymentSuccess &&
                (summary?.externalId || summary?.invoiceNumber || summary?.isInvoiceGenerated),
            ) && (
              <button
                type="button"
                className="view-invoice-btn"
                onClick={handleOpenInvoice}
                disabled={invoiceLoading}>
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round">
                  <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                  <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
                </svg>
                <span>{invoiceLoading ? t('loading') : t('needInvoice')}</span>
              </button>
            )}

            <button type="button" className="back-home-btn" onClick={cleanStorageAndRedirect}>
              {t('backToHome')}
            </button>
          </div>
        </div>
      )}

      {/* --- INVOICE DETAILS FORM MODAL --- */}
      {showInvoiceModal && (
        <div
          className="invoice-form-overlay"
          role="presentation"
          onClick={() => {
            if (!invoiceLoading) {
              setShowInvoiceModal(false);
              setIsCountryDropdownOpen(false);
            }
          }}>
          <div
            className="invoice-form-sheet"
            role="presentation"
            onClick={(event) => event.stopPropagation()}>
            <div className="sheet-drag-handle" />

            <div className="invoice-form-header">
              <div className="header-title-box">
                <div className="header-icon-box">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                    <polyline points="10 9 9 9 8 9" />
                  </svg>
                </div>
                <div>
                  <h2 className="modal-title">{t('invoiceDetailsTitle')}</h2>
                  <p className="modal-subtitle">{t('invoiceDetailsSubtitle')}</p>
                </div>
              </div>
              <button
                type="button"
                className="close-modal-btn"
                onClick={() => {
                  if (!invoiceLoading) {
                    setShowInvoiceModal(false);
                    setIsCountryDropdownOpen(false);
                  }
                }}
                aria-label="Close invoice form"
                disabled={invoiceLoading}>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <form className="invoice-modal-form" onSubmit={handleFormSubmit} noValidate>
              {/* Name Field */}
              <div className="form-field-group">
                <p className="form-label">
                  {t('fullName')} <span className="required-star">*</span>
                </p>
                <div className={`input-wrapper ${formErrors.name ? 'has-error' : ''}`}>
                  <input
                    id="invoice-name"
                    type="text"
                    className="form-input"
                    placeholder={t('fullNamePlaceholder')}
                    value={formData.name}
                    onChange={(event) => handleInputChange('name', event.target.value)}
                    disabled={invoiceLoading}
                  />
                </div>
                {formErrors.name && (
                  <p className="field-error-message">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="12" stroke="var(--white)" strokeWidth="2" />
                      <line
                        x1="12"
                        y1="16"
                        x2="12.01"
                        y2="16"
                        stroke="var(--white)"
                        strokeWidth="2"
                      />
                    </svg>
                    <span>{formErrors.name}</span>
                  </p>
                )}
              </div>

              {/* Email Field */}
              <div className="form-field-group">
                <p className="form-label">
                  {t('emailAddress')} <span className="required-star">*</span>
                </p>
                <div className={`input-wrapper ${formErrors.email ? 'has-error' : ''}`}>
                  <input
                    id="invoice-email"
                    type="email"
                    className="form-input"
                    placeholder={t('emailPlaceholder')}
                    value={formData.email}
                    onChange={(event) => handleInputChange('email', event.target.value)}
                    disabled={invoiceLoading}
                  />
                </div>
                {formErrors.email && (
                  <p className="field-error-message">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="12" stroke="var(--white)" strokeWidth="2" />
                      <line
                        x1="12"
                        y1="16"
                        x2="12.01"
                        y2="16"
                        stroke="var(--white)"
                        strokeWidth="2"
                      />
                    </svg>
                    <span>{formErrors.email}</span>
                  </p>
                )}
              </div>

              {/* Mobile Field with Country Code Dropdown */}
              <div className="form-field-group">
                <p className="form-label">
                  {t('mobileNumber')} <span className="required-star">*</span>
                </p>
                <div
                  className={`phone-input-container ${formErrors.mobile ? 'has-error' : ''}`}
                  ref={countryDropdownReference}>
                  {/* Country Selector Button */}
                  <button
                    type="button"
                    className="country-select-btn"
                    onClick={() => setIsCountryDropdownOpen((previous) => !previous)}
                    disabled={invoiceLoading}
                    aria-label="Select country code">
                    <span className="country-flag">{selectedCountry.flag}</span>
                    <span className="country-code">{selectedCountry.code}</span>
                    <svg
                      className={`chevron-icon ${isCountryDropdownOpen ? 'open' : ''}`}
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2">
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </button>

                  {/* Phone Input */}
                  <input
                    id="invoice-mobile"
                    type="tel"
                    className="phone-number-input"
                    placeholder={t('mobilePlaceholder')}
                    value={formData.mobile}
                    onChange={(event) => handleInputChange('mobile', event.target.value)}
                    disabled={invoiceLoading}
                  />

                  {/* Country Dropdown Menu */}
                  {isCountryDropdownOpen && (
                    <div className="country-dropdown-menu">
                      <div className="country-search-box">
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#9ca3af"
                          strokeWidth="2">
                          <circle cx="11" cy="11" r="8" />
                          <line x1="21" y1="21" x2="16.65" y2="16.65" />
                        </svg>
                        <input
                          type="text"
                          className="country-search-input"
                          placeholder={t('searchCountryPlaceholder')}
                          value={countrySearch}
                          onChange={(event) => setCountrySearch(event.target.value)}
                        />
                      </div>
                      <div className="country-options-list">
                        {filteredCountries.map((item) => (
                          <div
                            key={`${item.country}-${item.code}`}
                            className={`country-option-item ${
                              selectedCountry.code === item.code &&
                              selectedCountry.country === item.country
                                ? 'active'
                                : ''
                            }`}
                            onClick={() => {
                              setSelectedCountry(item);
                              setIsCountryDropdownOpen(false);
                              setCountrySearch('');
                            }}
                            role="presentation">
                            <span className="item-flag">{item.flag}</span>
                            <span className="item-name">{item.name}</span>
                            <span className="item-code">{item.code}</span>
                          </div>
                        ))}
                        {filteredCountries.length === 0 && (
                          <div className="no-countries-found">{t('noCountriesFound')}</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                {formErrors.mobile && (
                  <p className="field-error-message">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="12" stroke="var(--white)" strokeWidth="2" />
                      <line
                        x1="12"
                        y1="16"
                        x2="12.01"
                        y2="16"
                        stroke="var(--white)"
                        strokeWidth="2"
                      />
                    </svg>
                    <span>{formErrors.mobile}</span>
                  </p>
                )}
              </div>

              {/* Address Field (Optional) */}
              <div className="form-field-group">
                <div className="label-with-optional">
                  <p className="form-label">{t('billingAddress')}</p>
                  <span className="optional-badge">{t('optional')}</span>
                </div>
                <div className="input-wrapper">
                  <input
                    id="invoice-address"
                    type="text"
                    className="form-input"
                    placeholder={t('addressPlaceholder')}
                    value={formData.address}
                    onChange={(event) => handleInputChange('address', event.target.value)}
                    disabled={invoiceLoading}
                  />
                </div>
              </div>

              {/* Form Action Buttons */}
              <div className="form-action-buttons">
                <button type="submit" className="submit-form-btn" disabled={invoiceLoading}>
                  {invoiceLoading ? (
                    <>
                      <div className="btn-spinner" />
                      <span>{t('generatingInvoice')}</span>
                    </>
                  ) : (
                    <>
                      <svg
                        viewBox="0 0 24 24"
                        width="18"
                        height="18"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round">
                        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
                      </svg>
                      <span>{t('generateInvoice')}</span>
                    </>
                  )}
                </button>

                <button
                  type="button"
                  className="cancel-form-btn"
                  onClick={() => {
                    setShowInvoiceModal(false);
                    setIsCountryDropdownOpen(false);
                  }}
                  disabled={invoiceLoading}>
                  {t('cancel')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* --- 2. DETAILED TAX INVOICE VIEW --- */}
      {activeView === 'invoice_detail' && (
        <div className="invoice-detail-view">
          <div className="invoice-header-bar">
            <button
              type="button"
              className="back-arrow-btn"
              onClick={() => setActiveView('receipt')}
              aria-label="Back to receipt">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round">
                <line x1="19" y1="12" x2="5" y2="12" />
                <polyline points="12 19 5 12 12 5" />
              </svg>
            </button>
            <h2>{t('invoice')}</h2>
          </div>

          {/* Section: Invoice */}
          <div className="invoice-section-card">
            <h3 className="section-title">{t('invoice')}</h3>
            {renderRow(t('invoiceNo'), invoiceData?.invoiceNumber || summary?.invoiceNumber)}
            {renderRow(
              t('date'),
              invoiceData?.invoiceDate
                ? formatLocalTime(invoiceData.invoiceDate)
                : invoiceData?.chargingDetails?.chargingDate || formattedEndTime,
            )}
            {renderRow(t('financialYear'), invoiceData?.financialYear)}
            {renderRow(
              t('referenceNo'),
              invoiceData?.transactionId ||
                invoiceData?.sessionId ||
                summary?.externalId ||
                storedSessionId,
            )}
          </div>

          {/* Section: Seller */}
          {(isValidValue(invoiceData?.invoiceFrom?.name) ||
            isValidValue(invoiceData?.invoiceFrom?.email) ||
            isValidValue(invoiceData?.invoiceFrom?.billingAddress)) && (
            <div className="invoice-section-card">
              <h3 className="section-title">{t('seller')}</h3>
              {renderRow(t('name'), invoiceData?.invoiceFrom?.name)}
              {renderRow(t('mobile'), invoiceData?.invoiceFrom?.mobile)}
              {renderRow(t('email'), invoiceData?.invoiceFrom?.email)}
              {renderRow(t('billingAddress'), invoiceData?.invoiceFrom?.billingAddress)}
              {renderRow(t('gstin'), invoiceData?.invoiceFrom?.gstin)}
              {renderRow(t('cin'), invoiceData?.invoiceFrom?.cin)}
              {renderRow(t('sacCode'), invoiceData?.invoiceFrom?.sacCode)}
            </div>
          )}

          {/* Section: Buyer */}
          {(isValidValue(invoiceData?.invoiceTo?.name) ||
            isValidValue(invoiceData?.invoiceTo?.email) ||
            isValidValue(invoiceData?.invoiceTo?.mobile) ||
            isValidValue(invoiceData?.invoiceTo?.billingAddress) ||
            isValidValue(invoiceData?.invoiceTo?.address) ||
            isValidValue(invoiceData?.invoiceTo?.chargingLocation)) && (
            <div className="invoice-section-card">
              <h3 className="section-title">{t('buyer')}</h3>
              {renderRow(t('name'), invoiceData?.invoiceTo?.name)}
              {renderRow(t('mobile'), invoiceData?.invoiceTo?.mobile)}
              {renderRow(t('email'), invoiceData?.invoiceTo?.email)}
              {renderRow(
                t('address'),
                invoiceData?.invoiceTo?.billingAddress || invoiceData?.invoiceTo?.address,
              )}
              {renderRow(t('pan'), invoiceData?.invoiceTo?.pan)}
              {renderRow(t('chargingLocation'), invoiceData?.invoiceTo?.chargingLocation)}
            </div>
          )}

          {/* Section: Charging session */}
          <div className="invoice-section-card">
            <h3 className="section-title">{t('chargingSession')}</h3>
            {renderRow(t('charger'), invoiceData?.chargerInfo?.chargerId || deviceId)}
            {renderRow(
              t('connector'),
              [
                invoiceData?.chargerInfo?.connectorId,
                invoiceData?.chargerInfo?.connectorType,
                invoiceData?.chargerInfo?.connectorRating,
              ]
                .filter(isValidValue)
                .join(' / '),
            )}
            {renderRow(t('time'), invoiceSessionTime)}
            {renderRow(
              t('duration'),
              invoiceData?.chargingDetails?.chargingDuration || getDurationDisplay(),
            )}
            {renderRow(
              t('energy'),
              invoiceData?.chargingDetails?.energyUsed !== undefined
                ? `${invoiceData.chargingDetails.energyUsed} kWh`
                : energyKwhDisplay,
            )}
            {renderRow(t('vehicleVin'), invoiceData?.chargingDetails?.evVin)}
            {renderRow(t('vehicleNo'), invoiceData?.chargingDetails?.evNumber)}
            {renderRow(t('bookingId'), invoiceData?.chargingDetails?.bookingId)}
            {renderRow(
              t('remark'),
              invoiceData?.remark || invoiceData?.chargingDetails?.remark || summary?.remark,
            )}
          </div>

          {/* Section: Cost breakdown */}
          <div className="invoice-section-card cost-breakdown-section">
            <h3 className="section-title">{t('costBreakdown')}</h3>
            {renderCostRow(
              t('connectorPrice'),
              invoiceData?.costBreakdown?.connectorPrice,
              invoiceData?.chargingDetails?.costBreakdown?.currency ||
                invoiceData?.currency ||
                currencyCode,
            )}
            {renderCostRow(
              t('consumptionCost'),
              invoiceData?.chargingDetails?.costBreakdown?.consumptionCost ??
                invoiceData?.costBreakdown?.consumptionCost ??
                (summary?.totalCost !== undefined
                  ? Number(summary.totalCost).toFixed(2)
                  : undefined),
              invoiceData?.chargingDetails?.costBreakdown?.currency ||
                invoiceData?.currency ||
                currencyCode,
            )}
            {renderCostRow(
              t('platformFee'),
              invoiceData?.chargingDetails?.costBreakdown?.platformFee ??
                invoiceData?.costBreakdown?.platformFee,
              invoiceData?.chargingDetails?.costBreakdown?.currency ||
                invoiceData?.currency ||
                currencyCode,
            )}
            {renderCostRow(
              t('surchargeFee'),
              invoiceData?.chargingDetails?.costBreakdown?.paymentSurcharge ??
                invoiceData?.costBreakdown?.paymentSurcharge,
              invoiceData?.chargingDetails?.costBreakdown?.currency ||
                invoiceData?.currency ||
                currencyCode,
            )}
            <div className="total-divider" />
            <div className="total-row">
              <span>{t('total')}</span>
              <span>
                {invoiceData?.chargingDetails?.costBreakdown?.total ??
                  invoiceData?.chargingDetails?.invoiceAmountPaid ??
                  invoiceData?.costBreakdown?.totalAmount ??
                  getHeadlineAmount()}{' '}
                {invoiceData?.chargingDetails?.costBreakdown?.currency ||
                  invoiceData?.currency ||
                  currencyCode}
              </span>
            </div>
          </div>

          {/* Download Button */}
          <button type="button" className="download-pdf-btn" onClick={handleDownloadPdf}>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            <span>{t('downloadInvoice')}</span>
          </button>
        </div>
      )}
    </div>
  );
}

export default Invoice;
